import { useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FilePlus2, Upload } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Entrada, Rotulo } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, mensagemDeErro, post } from '../../nucleo/api';
import { nomeDeArquivo } from '../../nucleo/formato';

/**
 * D24 L2 (resposta 4 do Gustavo): o contrato antigo, já assinado fora do sistema, sobe como documento `contrato` ligado ao
 * contrato, sem gerar de novo. O arquivo sobe antes pelo `/anexos` (com o id que o documento vai ter), como no Documentos.
 */
export function AnexarAssinado({ contratoId, aoFechar }: { contratoId: string; aoFechar: () => void }) {
  const { t } = useTranslation('assinaturas');
  const qc = useQueryClient();
  const toast = useToast();
  const id = useMemo(() => crypto.randomUUID(), []);
  const seletor = useRef<HTMLInputElement>(null);
  const enviado = useRef<string | null>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [nome, setNome] = useState('');
  const anexar = useMutation({
    mutationFn: async () => {
      if (!enviado.current) {
        const fd = new FormData();
        fd.append('entidade', 'documentos.documento');
        fd.append('registro_id', id);
        fd.append('arquivo', arquivo!);
        enviado.current = (await api<{ id: string }>('POST', '/anexos', fd)).id;
      }
      return post(
        '/documentos/documentos',
        { id, anexo_id: enviado.current, nome: nome.trim() || undefined, registro_tipo: 'assinaturas.contrato', registro_id: contratoId, tipo: 'contrato', tags: [] },
        { 'Idempotency-Key': id },
      );
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['documentos'] });
      toast.sucesso(t('anexarAssinado.anexado'));
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={t('anexarAssinado.titulo')}
      rodape={
        <Botao variante="primario" icone={<FilePlus2 className="size-4" />} carregando={anexar.isPending} aria-disabled={!arquivo} onClick={() => (arquivo ? anexar.mutate() : toast.erro(t('anexarAssinado.arquivoObrigatorio')))}>
          {t('anexarAssinado.anexar')}
        </Botao>
      }
    >
      <div className="flex min-w-0 flex-col gap-4">
        <p className="text-sm text-tinta-3">{t('anexarAssinado.ajuda')}</p>
        <Rotulo id="assinado-nome" rotulo={t('anexarAssinado.nome')} ajuda={t('anexarAssinado.nomeAjuda')}>
          <Entrada id="assinado-nome" value={nome} maxLength={200} onChange={(e) => setNome(e.target.value)} />
        </Rotulo>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-tinta-2">{t('anexarAssinado.arquivo')}</span>
          <div className="relative flex min-w-0 flex-wrap items-center gap-3">
            <Botao icone={<Upload className="size-4" />} onClick={() => seletor.current?.click()}>
              {arquivo ? t('anexarAssinado.trocar') : t('anexarAssinado.escolher')}
            </Botao>
            {arquivo && (
              <span className="min-w-0 truncate text-sm text-tinta-2" title={arquivo.name}>
                {nomeDeArquivo(arquivo.name)}
              </span>
            )}
            {/* O input `sr-only` fica fora do Tab (regra r16); o botão visível é o controle. */}
            <input
              ref={seletor}
              type="file"
              accept="application/pdf,.pdf"
              className="sr-only"
              tabIndex={-1}
              aria-label={t('anexarAssinado.arquivo')}
              title={t('anexarAssinado.arquivo')}
              onChange={(e) => {
                enviado.current = null;
                setArquivo(e.target.files?.[0] ?? null);
              }}
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
}
